import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Input, Skeleton } from 'antd';
import {
  Wrench,
  Lightbulb,
  Smartphone,
  Laptop,
  House,
  Tv,
  Check,
  ArrowRight,
  Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PublicWebContentService from '../services/publicWebContentService';
import { storageUrl } from '../services/api';
import './Home.css';
import './Services.css';

const ICONS = [Wrench, Lightbulb, Smartphone, Laptop, House, Tv];

const defaultServices = [
  { icon: Wrench, title: 'Appliance Repair', description: 'Expert repair services for all household appliances.', features: ['Refrigerator Repair', 'Washing Machine Service', 'Microwave Maintenance', 'AC Repair'], price: 'Rs 1,500' },
  { icon: Lightbulb, title: 'Electrical Installation', description: 'Professional electrical wiring and lighting installation.', features: ['Home Wiring', 'Lighting Fixtures', 'Outlet Installation', 'Safety Inspections'], price: 'Rs 3,000' },
  { icon: Smartphone, title: 'Phone & Tablet Repair', description: 'Fast and reliable repair for smartphones and tablets.', features: ['Screen Replacement', 'Battery Service', 'Software Issues', 'Water Damage'], price: 'Rs 1,000' },
  { icon: Laptop, title: 'Computer Repair', description: 'Comprehensive repair and upgrade services for computers.', features: ['Hardware Repair', 'Software Installation', 'Virus Removal', 'Data Recovery'], price: 'Rs 2,000' },
  { icon: House, title: 'Home Electrical Maintenance', description: 'Preventive maintenance and safety inspections.', features: ['Annual Check-ups', 'Safety Inspections', 'System Upgrades', 'Emergency Repairs'], price: 'Rs 4,000' },
  { icon: Tv, title: 'TV Repair & Installation', description: 'Expert TV repair and wall mounting services.', features: ['Screen Repair', 'Wall Mounting', 'Cable Setup', 'Smart TV Configuration'], price: 'Rs 2,500' },
];

const Services = () => {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [customTitle, setCustomTitle] = useState('Need Custom Electrical Solutions?');
  const [customDescription, setCustomDescription] = useState(
    'Our team can create personalized service plans tailored to your specific requirements.',
  );
  const [services, setServices] = useState(defaultServices);

  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL || '/api';

  const requireAuth = (path) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate(path);
    } else {
      navigate('/login', { state: { from: path } });
    }
  };

  const sectionToMap = (items) => {
    const map = {};
    (items || []).forEach((item) => {
      map[item.content_key] = item.content_value;
    });
    return map;
  };

  useEffect(() => {
    const loadContent = async () => {
      try {
        const [pageContent, servicesRes] = await Promise.all([
          PublicWebContentService.getPageContent('services').catch(() => null),
          fetch(`${apiBase}/products-services?type=service&limit=20`)
            .then((r) => (r.ok ? r.json() : { data: [] }))
            .catch(() => ({ data: [] })),
        ]);

        if (pageContent) {
          const custom = sectionToMap(pageContent.custom_solutions);
          if (custom.title) setCustomTitle(custom.title);
          if (custom.description) setCustomDescription(custom.description);
        }

        const serviceData = servicesRes?.data?.data || servicesRes?.data || [];
        if (Array.isArray(serviceData) && serviceData.length > 0) {
          setServices(
            serviceData.map((s, i) => ({
              icon: ICONS[i % ICONS.length],
              title: s.name,
              description: s.description || 'Professional service',
              features: s.tags || [],
              price: s.price ? `Rs ${Number(s.price).toLocaleString()}` : 'Contact for price',
              image: storageUrl(s.images?.[0]?.url || s.images?.[0]?.image_url),
            })),
          );
        }
      } catch (error) {
        console.error('Failed to load services content:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  const q = searchQuery.trim().toLowerCase();
  const filteredServices = q
    ? services.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          (s.features || []).some((f) => f.toLowerCase().includes(q)),
      )
    : services;

  if (loading) {
    return (
      <div style={{ padding: '40px' }}>
        <Skeleton active paragraph={{ rows: 12 }} />
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Blue page header band with search */}
      <section className="cc-band cat-band">
        <div className="cc-band-inner">
          <span className="cc-band-eyebrow">Book a service</span>
          <h1>Services</h1>
          <p className="cc-band-sub">
            Expert installation, maintenance and support from certified technicians across Nepal.
          </p>
          <div className="cat-search">
            <Input
              size="large"
              allowClear
              placeholder="Search services..."
              prefix={<Search size={17} style={{ color: '#94a3b8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Service categories */}
      <section className="home-section">
        <div className="cat-toolbar">
          <div className="cat-toolbar-title">
            <h2>Our Professional Services</h2>
            <p>
              Comprehensive electrical and electronics services delivered by certified technicians
              with transparent pricing.
            </p>
          </div>
        </div>
        <Row gutter={[24, 24]}>
          {filteredServices.map((service, idx) => {
            const Icon = service.icon || ICONS[idx % ICONS.length];
            return (
              <Col xs={24} md={12} lg={8} key={service.title + idx}>
                <div className="svc-card">
                  <span className="svc-icon">
                    <Icon size={26} strokeWidth={2} />
                  </span>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  {service.features && service.features.length > 0 && (
                    <ul className="svc-features">
                      {service.features.map((feature) => (
                        <li key={feature}>
                          <Check size={16} strokeWidth={2.5} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="svc-foot">
                    <div className="svc-price">
                      <small>Starting at</small>
                      {service.price}
                    </div>
                    <Button
                      type="primary"
                      block
                      style={{ marginTop: 14, height: 44, fontWeight: 600, borderRadius: 12 }}
                      onClick={() => requireAuth('/service-catalog')}
                    >
                      Book Service
                    </Button>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
        {filteredServices.length === 0 && (
          <p style={{ textAlign: 'center', color: '#64748b', paddingTop: 8 }}>
            No services match your search.
          </p>
        )}
      </section>

      {/* Custom solutions CTA */}
      <section className="home-section">
        <div className="cc-cta">
          <h2>{customTitle}</h2>
          <p>{customDescription}</p>
          <div className="cc-cta-actions">
            <Button className="btn-amber" size="large" onClick={() => navigate('/contact')}>
              Schedule Consultation
            </Button>
            <Button
              className="btn-ghost"
              size="large"
              onClick={() => requireAuth('/service-catalog')}
            >
              Book a Service <ArrowRight size={16} style={{ verticalAlign: -3, marginLeft: 4 }} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
